import { useCallback, useContext, useMemo } from "react";
import type { ExperimentsType } from "@hopper-b2b/types";
import { experimentsApi } from "../store/api";
import type { Experiments } from "./experimentsList";
import { UserContext } from "@/App";

type ExperimentKeys = keyof typeof Experiments;

type ExperimentNames = {
  [K in ExperimentKeys]: (typeof Experiments)[K]["name"];
};

type ExperimentVariants = {
  [K in ExperimentKeys]: keyof (typeof Experiments)[K]["variants"];
};

const variantMatches = (fromApi: string, expected: string) =>
  fromApi.toLowerCase() === expected.toLowerCase();

export const useExperiment = () => {
  const { sessionInfo } = useContext(UserContext);
  const isSignedIn = sessionInfo?.userScope?.isSignedIn ?? false;
  const { data, isLoading, isFetching } = experimentsApi.useGetExperimentsQuery(
    undefined,
    {
      skip: !isSignedIn,
    }
  );

  const getExperimentVariant = useCallback(
    <T extends ExperimentKeys>(name: ExperimentNames[T]) =>
      data?.experiments.find((experiment) => experiment.id === name)?.variant,
    [data]
  );

  const isExperimentVariant = useCallback(
    <T extends ExperimentKeys>(
      name: ExperimentNames[T],
      variant: ExperimentVariants[T]
    ) =>
      !!data?.experiments.some(
        (experiment) =>
          experiment.id === name &&
          variantMatches(String(experiment.variant), String(variant))
      ),
    [data]
  );

  const experiments = useMemo(
    () =>
      data?.experiments.reduce((acc, experiment) => {
        acc[experiment.id] = experiment.variant;
        return acc;
      }, {} as ExperimentsType),
    [data]
  );

  const getExperimentJsonPayload = useCallback(
    <T = unknown>(experimentId: string): T | undefined => {
      const experiment = data?.experiments.find(
        (exp) => exp.id === experimentId
      );
      return experiment?.jsonPayload as T | undefined;
    },
    [data]
  );

  return {
    getExperimentVariant,
    isExperimentVariant,
    getExperimentJsonPayload,
    experiments,
    data,
    isLoading: isLoading || isFetching,
  };
};
